import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator
} from 'react-native';
import tw from 'twrnc';

interface ConfirmationModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText: string;
  cancelText?: string;
  isLoading?: boolean;
  confirmButtonStyle?: 'primary' | 'danger' | 'success';
  icon?: string;
  iconColor?: string;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  visible,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText = 'Cancel',
  isLoading = false,
  confirmButtonStyle = 'primary',
  icon,
  iconColor
}) => {
  const getConfirmButtonStyles = () => {
    switch (confirmButtonStyle) {
      case 'danger':
        return 'bg-red-600';
      case 'success':
        return 'bg-green-600';
      case 'primary':
      default:
        return 'bg-blue-600';
    }
  };

  const getIconName = () => {
    if (icon) return icon;

    switch (confirmButtonStyle) {
      case 'danger':
        return 'warning-outline';
      case 'success':
        return 'checkmark-circle-outline';
      case 'primary':
      default:
        return 'information-circle-outline';
    }
  };

  const getIconColor = () => {
    if (iconColor) return iconColor;

    switch (confirmButtonStyle) {
      case 'danger':
        return '#EF4444';
      case 'success':
        return '#10B981';
      case 'primary':
      default:
        return '#3B82F6';
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      {/* Backdrop */}
      <View style={tw`flex-1 bg-black bg-opacity-50 justify-center items-center px-4`}>
        {/* Modal Content */}
        <View style={tw`bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl`}>

          {/* Icon */}
          <View style={tw`items-center mb-4`}>
            <View style={tw`w-16 h-16 rounded-full bg-gray-100 items-center justify-center`}>
              <Ionicons
                name={getIconName() as any}
                size={32}
                color={getIconColor()}
              />
            </View>
          </View>

          {/* Title */}
          <Text style={tw`text-xl font-bold text-gray-900 text-center mb-3`}>
            {title}
          </Text>

          {/* Message */}
          <Text style={tw`text-gray-600 text-center leading-6 mb-6`}>
            {message}
          </Text>

          {/* Action Buttons */}
          <View style={tw`flex-row gap-3`}>
            {/* Cancel Button */}
            <TouchableOpacity
              onPress={onClose}
              disabled={isLoading}
              style={tw`flex-1 py-3 px-4 rounded-xl border-2 border-gray-200 bg-gray-50 ${isLoading ? 'opacity-50' : ''}`}
            >
              <Text style={tw`text-gray-700 font-semibold text-center`}>
                {cancelText}
              </Text>
            </TouchableOpacity>

            {/* Confirm Button */}
            <TouchableOpacity
              onPress={onConfirm}
              disabled={isLoading}
              style={tw`flex-1 py-3 px-4 rounded-xl ${getConfirmButtonStyles()} ${isLoading ? 'opacity-75' : ''}`}
            >
              {isLoading ? (
                <View style={tw`flex-row items-center justify-center`}>
                  <ActivityIndicator size="small" color="white" />
                  <Text style={tw`text-white font-semibold ml-2`}>
                    Processing...
                  </Text>
                </View>
              ) : (
                <Text style={tw`text-white font-semibold text-center`}>
                  {confirmText}
                </Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};